export const PAN = /^[A-Z]{5}[0-9]{4}[A-Z]$/
export const GSTIN = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/
export const PINCODE = /^[1-9][0-9]{5}$/
export const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
/** Indian mobile/landline national number, once the dial code is stripped off. */
export const INDIAN_NUMBER = /^[6-9][0-9]{9}$/

export const GST_STATE_CODES: Record<string, string> = {
    '01': 'Jammu and Kashmir',
    '02': 'Himachal Pradesh',
    '03': 'Punjab',
    '04': 'Chandigarh',
    '05': 'Uttarakhand',
    '06': 'Haryana',
    '07': 'Delhi',
    '08': 'Rajasthan',
    '09': 'Uttar Pradesh',
    '10': 'Bihar',
    '11': 'Sikkim',
    '12': 'Arunachal Pradesh',
    '13': 'Nagaland',
    '14': 'Manipur',
    '15': 'Mizoram',
    '16': 'Tripura',
    '17': 'Meghalaya',
    '18': 'Assam',
    '19': 'West Bengal',
    '20': 'Jharkhand',
    '21': 'Odisha',
    '22': 'Chhattisgarh',
    '23': 'Madhya Pradesh',
    '24': 'Gujarat',
    '25': 'Daman and Diu',
    '26': 'Dadra and Nagar Haveli and Daman and Diu',
    '27': 'Maharashtra',
    '28': 'Andhra Pradesh',
    '29': 'Karnataka',
    '30': 'Goa',
    '31': 'Lakshadweep',
    '32': 'Kerala',
    '33': 'Tamil Nadu',
    '34': 'Puducherry',
    '35': 'Andaman and Nicobar Islands',
    '36': 'Telangana',
    '37': 'Andhra Pradesh',
    '38': 'Ladakh',
    '97': 'Other Territory',
}

const CHECKSUM_ALPHABET = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ'

/**
 * GSTIN check digit (mod-36, alternating weights 1/2 with digit-sum folding).
 * Catches transposed or mistyped characters that still satisfy the shape regex.
 */
export const gstinChecksum = (gstin: string): boolean => {
    if (!/^[0-9A-Z]{15}$/.test(gstin)) return false

    let total = 0
    for (let i = 0; i < 14; i += 1) {
        const value = CHECKSUM_ALPHABET.indexOf(gstin[i])
        if (value < 0) return false
        const weighted = value * (i % 2 === 0 ? 1 : 2)
        total += Math.floor(weighted / 36) + (weighted % 36)
    }

    const expected = CHECKSUM_ALPHABET[(36 - (total % 36)) % 36]
    return expected === gstin[14]
}

/** Strip anything that can't appear in a PAN/GSTIN and force upper case. */
export const normaliseAlnum = (raw: string, maxLength: number): string =>
    raw
        .toUpperCase()
        .replace(/[^0-9A-Z]/g, '')
        .slice(0, maxLength)

export const digitsOnly = (raw: string, maxLength = 15): string =>
    raw.replace(/[^0-9]/g, '').slice(0, maxLength)

/** GSTIN characters 3–12 are the holder's PAN. */
export const gstinPan = (gstin: string): string => gstin.slice(2, 12)

export const stateFromGstin = (gstin: string): string | null =>
    GST_STATE_CODES[gstin.slice(0, 2)] ?? null

export const validatePan = (value: string): true | string => {
    if (!value) return 'PAN number is required'
    if (!PAN.test(value)) return 'PAN must look like ABCDE1234F'
    return true
}

export const validateGstin = (
    value: string,
    panValue?: string
): true | string => {
    if (!value) return 'GST number is required'
    if (!GSTIN.test(value)) return 'GSTIN must be 15 characters, e.g. 27ABCDE1234F1Z5'
    if (!gstinChecksum(value)) return 'That GSTIN check digit is invalid — please re-check'
    if (panValue && PAN.test(panValue) && gstinPan(value) !== panValue) {
        return 'This GSTIN belongs to a different PAN'
    }
    return true
}

export const validateEmail = (value: string): true | string => {
    if (!value) return 'Email is required'
    if (!EMAIL.test(value)) return 'Enter a valid email address'
    return true
}

/**
 * Validates the national number only — the country code is chosen separately, so
 * the strict Indian rule applies solely when +91 is selected.
 */
export const validatePhoneNumber = (
    number: string,
    dial: string,
    required = true
): true | string => {
    if (!number) return required ? 'Phone number is required' : true
    if (dial === '+91') {
        return INDIAN_NUMBER.test(number)
            ? true
            : 'Enter a 10-digit Indian number starting 6–9'
    }
    return /^[0-9]{4,14}$/.test(number) ? true : 'Enter a valid phone number'
}

export const validatePincode = (value: string): true | string => {
    if (!value) return 'PIN code is required'
    if (!PINCODE.test(value)) return 'PIN code must be 6 digits'
    return true
}
